import React from "react";
import "./Strategies.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlassChart,
  faMessage,
  faGift,
  faPenNib,
  faRobot,
  faGlobe,
  faGaugeHigh,
  faHandshakeAngle,
} from "@fortawesome/free-solid-svg-icons";

const strategyItems = [
  {
    icon: faMagnifyingGlassChart,
    title: "Check where you are today",
    layer: "Step 1 · Check-up",
    description:
      "We start by looking at your website, Google listing, ads and basic numbers. The goal is simple: see what is helping, what is hurting and where money is being wasted.",
    tags: ["Website check", "Google listing", "Ad review"],
  },
  {
    icon: faPenNib,
    title: "Fix your main message & offers",
    layer: "Step 2 · Message",
    description:
      "We turn what you already do into clear, simple offers that normal people understand. This same message goes on your website, ads and Google listing so everything feels joined-up.",
    tags: ["Clear offers", "Simple language", "Same story everywhere"],
  },
  {
    icon: faGlobe,
    title: "Make your website easy to use",
    layer: "Step 3 · Website basics",
    description:
      "We tidy your key pages so visitors quickly see who you are, what you do and how to contact you. Clean layout, strong proof and buttons that are easy to find on mobile.",
    tags: ["Home & service pages", "Mobile friendly", "Clear buttons"],
  },
  {
    icon: faGaugeHigh,
    title: "Set up Google & Meta ads the right way",
    layer: "Step 4 · Ads",
    description:
      "We create or fix your Google and Facebook/Instagram ads so they stop chasing random clicks and start bringing calls, messages and form enquiries from the right people.",
    tags: ["Google Ads", "Facebook / Instagram", "Real enquiries"],
  },
  {
    icon: faGift,
    title: "Match ads to strong landing pages",
    layer: "Step 5 · Click-to-call",
    description:
      "We connect your ads to simple pages that continue the same promise. No surprises, no confusion — just a straight path from click to call, message or booking.",
    tags: ["Landing pages", "Same promise", "Less drop-off"],
  },
  {
    icon: faRobot,
    title: "Stay in touch with past visitors",
    layer: "Step 6 · Follow-ups",
    description:
      "We set up friendly email or WhatsApp follow-ups so people who showed interest don’t forget you. Light reminders, not spam — enough to bring them back when they’re ready.",
    tags: ["Email/WhatsApp", "Warm leads", "Repeat business"],
  },
  {
    icon: faMagnifyingGlassChart,
    title: "Help people find you on Google (SEO)",
    layer: "Step 7 · SEO",
    description:
      "We fix key SEO basics and plan simple pages and articles around the words your customers type into Google, so you show up more often for the searches that matter.",
    tags: ["SEO basics", "Useful content", "Local & niche search"],
  },
  {
    icon: faHandshakeAngle,
    title: "Simple reports & next steps",
    layer: "Step 8 · Review",
    description:
      "Each month, we share a short update: what we did, what changed and what we’ll do next. No heavy reports — just clear numbers and plain-language next steps.",
    tags: ["Easy reports", "Clear numbers", "Next month plan"],
  },
];

const Strategies = () => {
  return (
    <section
      className="strat2"
      aria-label="Our ads & SEO strategy"
    >
      <div className="strat2-wrap">
        {/* Header */}
        <header className="strat2-head">
          <p className="strat2-kicker">How we run your ads & SEO</p>
          <h2>One simple system, from “found online” to “new customer”.</h2>
          <p className="strat2-sub">
            We don’t just switch on ads and hope. We follow a clear, step-by-step
            system: fix the basics on your website, set up Google & Meta ads
            properly, help you show up on Google search, and keep past visitors
            coming back.
          </p>

          <div className="strat2-pills" aria-hidden="true">
            <span className="pill">Fix website basics first</span>
            <span className="pill">Google & Meta ads that convert</span>
            <span className="pill">SEO & follow-ups for long-term results</span>
          </div>
        </header>

        {/* Grid */}
        <div className="strat2-grid" role="list">
          {strategyItems.map((item, i) => (
            <article className="strat2-card" role="listitem" key={item.title}>
              <div className="strat2-card-rail" aria-hidden="true" />

              <div className="strat2-card-header">
                <div className="strat2-icon-wrap">
                  <div className="strat2-icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <span className="strat2-layer">{item.layer}</span>
                </div>
                <span className="strat2-index">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>

              <h3>{item.title}</h3>
              <p>{item.description}</p>

              <div className="strat2-tags">
                {item.tags.map((t) => (
                  <span className="tag" key={t}>
                    {t}
                  </span>
                ))}
              </div>

              <div className="strat2-cta" aria-hidden="true">
                See how this fits your ads & SEO plan →
              </div>
            </article>
          ))}
        </div>

        {/* Footnote strip */}
        <div className="strat2-strip">
          <span className="strat2-strip-label">How we like to work</span>
          <span className="strat2-strip-text">
            No tricks or secret dashboards • Clear ads & SEO basics first •
            Simple reports you can read in a few minutes
          </span>
        </div>
      </div>
    </section>
  );
};

export default Strategies;
